import { useState } from "react";
import { MessageCircle, Check, Send } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, pick, waLink } from "@/i18n";
import { useAuth } from "@/auth";
import { track } from "@/utils/track";
import FavoriteButton from "@/components/FavoriteButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingWidget({ exp, addons }) {
  const { lang, t } = useLanguage();
  const { user } = useAuth();
  const [date, setDate] = useState("");
  const [participants, setParticipants] = useState(2);
  const [slot, setSlot] = useState("");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const title = pick(exp.title, lang);

  const toggleAddon = (slug) =>
    setSelectedAddons((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]));

  const waMessage = () => {
    const lines = [
      `${t("wa_book_prefix")} ${title}`,
      date ? `${t("choose_date")}: ${date}` : null,
      `${t("participants")}: ${participants}`,
      slot ? `${t("time_slot")}: ${slot}` : null,
      selectedAddons.length ? `${t("addons_label")}: ${selectedAddons.map((s) => pick(addons.find((a) => a.slug === s)?.name, lang)).join(", ")}` : null,
    ].filter(Boolean);
    return waLink(lines.join("\n"));
  };

  const submit = async () => {
    if (!user && (!guestName || !guestEmail)) {
      toast(t("login_to_save"));
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${API}/booking-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          experience_slug: exp.slug,
          date,
          time_slot: slot,
          participants,
          addons: selectedAddons,
          name: guestName || undefined,
          email: guestEmail || undefined,
          language: lang,
        }),
      });
      if (!r.ok) throw new Error();
      setSent(true);
      track("booking_request", { slug: exp.slug, participants });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Error");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="sticky top-28 border border-sage/60 bg-ivory p-8 text-center" data-testid="booking-success">
        <div className="w-14 h-14 rounded-full bg-sage/20 flex items-center justify-center mx-auto mb-5">
          <Check size={24} strokeWidth={1.5} className="text-sage" />
        </div>
        <h3 className="font-serif text-2xl text-ink">{t("request_sent")}</h3>
        <p className="mt-3 text-sm text-ink/60 leading-relaxed">{t("request_sent_sub")}</p>
        <a href={waMessage()} target="_blank" rel="noopener noreferrer" data-testid="booking-success-whatsapp"
          className="mt-6 inline-flex items-center gap-2 bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.15em] px-6 py-3.5 hover:bg-ink transition-colors duration-300">
          <MessageCircle size={14} strokeWidth={1.5} /> {t("whatsapp_context")}
        </a>
      </div>
    );
  }

  return (
    <div className="sticky top-28 border border-stone2/60 bg-ivory p-7" id="booking-widget" data-testid="booking-widget">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-ink/40">{t("booking_form_title")}</p>
          <p className="mt-1.5 text-xs text-ink/50 leading-relaxed">{t("booking_form_sub")}</p>
        </div>
        <FavoriteButton slug={exp.slug} />
      </div>

      <div className="mt-6 space-y-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">{t("choose_date")}</label>
          <input type="date" value={date} min={new Date().toISOString().split("T")[0]} onChange={(e) => setDate(e.target.value)}
            data-testid="booking-date"
            className="mt-1.5 w-full border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">{t("participants")}</label>
            <select value={participants} onChange={(e) => setParticipants(Number(e.target.value))}
              data-testid="booking-participants"
              className="mt-1.5 w-full border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue">
              {[...Array(10)].map((_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">{t("time_slot")}</label>
            <select value={slot} onChange={(e) => setSlot(e.target.value)}
              data-testid="booking-slot"
              className="mt-1.5 w-full border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue">
              <option value="">—</option>
              <option value={t("slot_morning")}>{t("slot_morning")}</option>
              <option value={t("slot_afternoon")}>{t("slot_afternoon")}</option>
              <option value={t("slot_evening")}>{t("slot_evening")}</option>
            </select>
          </div>
        </div>

        {addons.length > 0 && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">{t("addons_label")}</label>
            <div className="mt-2 space-y-2">
              {addons.map((a) => (
                <label key={a.slug} className="flex items-center gap-2.5 text-sm text-ink/70 cursor-pointer" data-testid={`booking-addon-${a.slug}`}>
                  <input type="checkbox" checked={selectedAddons.includes(a.slug)} onChange={() => toggleAddon(a.slug)} className="w-4 h-4 accent-[#1D4E89]" />
                  {pick(a.name, lang)}
                </label>
              ))}
            </div>
          </div>
        )}

        {!user && (
          <div className="grid grid-cols-1 gap-3 pt-2 border-t border-stone2/50">
            <input placeholder={t("name_label")} value={guestName} onChange={(e) => setGuestName(e.target.value)}
              data-testid="booking-guest-name"
              className="border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue" />
            <input type="email" placeholder={t("email_label")} value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)}
              data-testid="booking-guest-email"
              className="border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue" />
          </div>
        )}

        <button onClick={submit} disabled={sending}
          data-testid="book-now-button"
          className="w-full flex items-center justify-center gap-2 bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.2em] px-6 py-4 hover:bg-ink disabled:opacity-40 transition-colors duration-300">
          <Send size={14} strokeWidth={1.5} /> {sending ? "..." : t("send_request")}
        </button>
        <a href={waMessage()} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { slug: exp.slug })}
          data-testid="whatsapp-booking-button"
          className="w-full flex items-center justify-center gap-2 border border-ink text-ink text-xs font-medium uppercase tracking-[0.2em] px-6 py-4 hover:bg-ink hover:text-ivory transition-colors duration-300">
          <MessageCircle size={14} strokeWidth={1.5} /> {t("whatsapp_context")}
        </a>
      </div>
    </div>
  );
}
